const baseUrl = import.meta.env.VITE_API_BASE_URL;
const productRuntime = import.meta.env.VITE_PRODUCT_RUNTIME;
const productReadOnly = /^(1|true|yes|on)$/i.test(import.meta.env.VITE_PRODUCT_READ_ONLY ?? '');

if (!baseUrl) {
  throw new Error('VITE_API_BASE_URL is not configured');
}

let redirectingToLogin = false;
const handleUnauthorized = () => {
  if (redirectingToLogin) return;
  redirectingToLogin = true;
  try {
    localStorage.removeItem('coop_token');
  } catch {
    // ignore
  }
  if (typeof window !== 'undefined') {
    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const params = new URLSearchParams();
    if (productRuntime) {
      if (!window.location.pathname.startsWith('/login')) params.set('from', current);
      const target = `/login${params.toString() ? `?${params.toString()}` : ''}`;
      window.location.replace(target);
      return;
    }
    if (!window.location.pathname.startsWith('/staff/portal')) {
      params.set('from', current);
    }
    const target = `/staff/portal${params.toString() ? `?${params.toString()}` : ''}`;
    window.location.replace(target);
  }
};

const buildUrl = (path: string) => `${baseUrl}${path}`;

type ApiRequestError = Error & {
  status: number;
  correlationId?: string | null;
  body?: unknown;
};

const createApiError = (message: string, status: number, correlationId?: string | null, body?: unknown): ApiRequestError => {
  const error = new Error(message) as ApiRequestError;
  error.status = status;
  error.correlationId = correlationId;
  if (body !== undefined) error.body = body;
  return error;
};

const productReadOnlyMutationAllowed = (path: string) =>
  path.startsWith('/auth/') || path.endsWith('/admin/routes/resolve');

const assertProductMutationAllowed = (path: string) => {
  if (!productRuntime || !productReadOnly || productReadOnlyMutationAllowed(path)) return;
  throw createApiError('Product console is read-only', 423, null, {
    error: 'product_ui_read_only',
    productRuntime,
  });
};

const getStoredToken = () => {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem('coop_token');
  } catch {
    return null;
  }
};

const withAuthHeaders = (headers?: HeadersInit): HeadersInit | undefined => {
  const token = getStoredToken();
  if (!token) return headers;
  return { Authorization: `Bearer ${token}`, ...(headers || {}) };
};

export const authorizedHeaders = (token?: string): HeadersInit | undefined =>
  token ? { Authorization: `Bearer ${token}` } : undefined;

export type ApiGetOptions = { redirectOnUnauthorized?: boolean };

export async function apiGet<T>(path: string, headers?: HeadersInit, options?: ApiGetOptions): Promise<T> {
  const response = await fetch(buildUrl(path), {
    headers: withAuthHeaders(headers),
    cache: 'no-store',
  });

  if (!response.ok) {
    if (response.status === 401 && options?.redirectOnUnauthorized !== false) handleUnauthorized();
    const correlationId = response.headers.get('x-correlation-id');
    let body: unknown;
    try {
      body = await response.json();
    } catch {
      body = undefined;
    }
    throw createApiError(`GET ${path} failed with status ${response.status}`, response.status, correlationId, body);
  }

  return (await response.json()) as T;
}

export async function apiGetBinary(path: string, headers?: HeadersInit): Promise<ArrayBuffer> {
  const response = await fetch(buildUrl(path), {
    headers: withAuthHeaders(headers),
    cache: 'no-store',
  });

  if (!response.ok) {
    if (response.status === 401) handleUnauthorized();
    const correlationId = response.headers.get('x-correlation-id');
    let body: unknown;
    try {
      body = await response.json();
    } catch {
      body = undefined;
    }
    throw createApiError(`GET ${path} failed with status ${response.status}`, response.status, correlationId, body);
  }

  return await response.arrayBuffer();
}

export async function apiPost<T>(path: string, body?: unknown, headers?: HeadersInit): Promise<T> {
  assertProductMutationAllowed(path);
  const mergedHeaders: HeadersInit | undefined =
    body !== undefined && !(body instanceof FormData)
      ? { 'Content-Type': 'application/json', ...(headers || {}) }
      : headers;

  const response = await fetch(buildUrl(path), {
    method: 'POST',
    headers: withAuthHeaders(mergedHeaders),
    body:
      body instanceof FormData
        ? body
        : body !== undefined
          ? JSON.stringify(body)
          : undefined,
    cache: 'no-store',
  });

  if (!response.ok) {
    if (response.status === 401) handleUnauthorized();
    let parsed: unknown = null;
    try {
      parsed = await response.json();
    } catch {
      parsed = null;
    }
    throw createApiError(
      `POST ${path} failed with status ${response.status}`,
      response.status,
      response.headers.get('x-correlation-id'),
      parsed ?? undefined,
    );
  }

  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export async function apiPut<T>(path: string, body?: unknown, headers?: HeadersInit): Promise<T> {
  assertProductMutationAllowed(path);
  const mergedHeaders: HeadersInit | undefined =
    body !== undefined
      ? { 'Content-Type': 'application/json', ...(headers || {}) }
      : headers;

  const response = await fetch(buildUrl(path), {
    method: 'PUT',
    headers: withAuthHeaders(mergedHeaders),
    body: body !== undefined ? JSON.stringify(body) : undefined,
    cache: 'no-store',
  });

  if (!response.ok) {
    if (response.status === 401) handleUnauthorized();
    throw new Error(`PUT ${path} failed with status ${response.status}`);
  }

  return (await response.json()) as T;
}

export async function apiPatch<T>(path: string, body?: unknown, headers?: HeadersInit): Promise<T> {
  assertProductMutationAllowed(path);
  const mergedHeaders: HeadersInit | undefined =
    body !== undefined
      ? { 'Content-Type': 'application/json', ...(headers || {}) }
      : headers;

  const response = await fetch(buildUrl(path), {
    method: 'PATCH',
    headers: withAuthHeaders(mergedHeaders),
    body: body !== undefined ? JSON.stringify(body) : undefined,
    cache: 'no-store',
  });

  if (!response.ok) {
    if (response.status === 401) handleUnauthorized();
    let parsed: unknown = null;
    try {
      parsed = await response.json();
    } catch {
      parsed = null;
    }
    throw createApiError(
      `PATCH ${path} failed with status ${response.status}`,
      response.status,
      response.headers.get('x-correlation-id'),
      parsed ?? undefined,
    );
  }

  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export async function apiDelete<T = void>(path: string, headers?: HeadersInit): Promise<T> {
  assertProductMutationAllowed(path);
  const response = await fetch(buildUrl(path), {
    method: 'DELETE',
    headers: withAuthHeaders(headers),
    cache: 'no-store',
  });

  if (!response.ok) {
    if (response.status === 401) handleUnauthorized();
    throw new Error(`DELETE ${path} failed with status ${response.status}`);
  }

  const hasJsonBody =
    response.status !== 204 &&
    response.headers.get('content-type')?.includes('application/json') &&
    response.headers.get('content-length') !== '0';

  if (hasJsonBody) {
    return (await response.json()) as T;
  }

  return undefined as T;
}

export async function apiDeleteWithBody<T = void>(
  path: string,
  body?: unknown,
  headers?: HeadersInit,
): Promise<T> {
  const mergedHeaders: HeadersInit | undefined =
    body !== undefined
      ? { 'Content-Type': 'application/json', ...(headers || {}) }
      : headers;

  const response = await fetch(buildUrl(path), {
    method: 'DELETE',
    headers: withAuthHeaders(mergedHeaders),
    body: body !== undefined ? JSON.stringify(body) : undefined,
    cache: 'no-store',
  });

  if (!response.ok) {
    if (response.status === 401) handleUnauthorized();
    throw new Error(`DELETE ${path} failed with status ${response.status}`);
  }

  const hasJsonBody =
    response.status !== 204 &&
    response.headers.get('content-type')?.includes('application/json') &&
    response.headers.get('content-length') !== '0';

  if (hasJsonBody) {
    return (await response.json()) as T;
  }

  return undefined as T;
}
